import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

// Simple item ID generation
const generateSimpleItemId = (itemName) => {
  return itemName
    .replace(/掛物/g, 'kakemono')
    .replace(/幸福/g, 'kofuku')
    .replace(/文/g, 'bun')
    .replace(/曽呂利/g, 'sorori')
    .replace(/新左衛門/g, 'shinzaemon')
    .replace(/利休/g, 'rikyu')
    .replace(/茶碗/g, 'chawan')
    .replace(/花入/g, 'hanaire')
    .replace(/香合/g, 'kogo')
    .toLowerCase()
    .replace(/[（）()]/g, '-')
    .replace(/[→]/g, '-to-')
    .replace(/[『』「」]/g, '-')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 50);
};

const PersonDetailMinimal = () => {
  const { personId } = useParams();
  const navigate = useNavigate();
  const [person, setPerson] = useState(null);
  const [itemsMap, setItemsMap] = useState({});
  const [itemSlugs, setItemSlugs] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState([]);
  const [personSlugs, setPersonSlugs] = useState({});

  useEffect(() => {
    console.log('PersonDetailMinimal: Loading for ID:', personId);

    // Simple direct fetch without hooks
    const loadData = async () => {
      try {
        const [peopleRes, itemsRes, itemSlugsRes, personSlugsRes] = await Promise.all([
          fetch('/people.json'),
          fetch('/items.json'),
          fetch('/item-slugs.json'),
          fetch('/person-slugs.json')
        ]);

        const [people, items, itemSlugsData, personSlugsData] = await Promise.all([
          peopleRes.json(),
          itemsRes.json(),
          itemSlugsRes.json(),
          personSlugsRes.json()
        ]);

        setItemsMap(items || {});
        setItemSlugs(itemSlugsData || {});
        setPersonSlugs(personSlugsData || {});

        console.log('PersonDetailMinimal: Loaded', people.length, 'people');

        const debugList = [];
        debugList.push(`Looking for ID: ${personId}`);
        debugList.push(`Found ${people.length} people in data`);

        // Support known legacy alias slugs → canonical person slug
        const personSlugAliases = {
          'raku-jikiny': 'raku-kichizaemon-xvi-atsuhito-1981',
          'raku-jikinyu': 'raku-kichizaemon-xvi-atsuhito-1981',
          'nakajima-sko': 'nakajima-soko-chaya-shirojiro-1584'
        };
        const incomingId = personId;
        const effectiveId = personSlugAliases[incomingId] || incomingId;

        // First try stored slugs mapping
        let found = null;
        if (personSlugsData && Object.keys(personSlugsData).length > 0) {
          const displayKey = Object.keys(personSlugsData).find(k => personSlugsData[k] === effectiveId);
          if (displayKey) {
            const [jp, romaji] = displayKey.match(/^(.*) \((.*)\)$/)?.slice(1) || [];
            const norm = (s) => (s || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
            found = people.find(p => p.name_jp === jp || p.name_romaji === romaji || norm(p.name_romaji) === norm(romaji)) || null;
          }
        }

        // Legacy tolerant fallback (prefix normalized match)
        if (!found) {
          const stripTrailing = (s) => (s || '').replace(/-+$/,'');
          const removeVowels = (s) => (s || '').replace(/[aeiou]/g, '');
          const stripHyphens = (s) => (s || '').replace(/-/g, '');
          const normalizeForPrefix = (s) => stripHyphens(removeVowels((s || '').toLowerCase()));

          const targetRaw = stripTrailing(effectiveId || '');
          const target = targetRaw.toLowerCase();
          const targetNoVowels = removeVowels(target);
          const targetCanonical = normalizeForPrefix(targetRaw);

          found = people.find(p => {
            const base = (p.name_romaji || '')
              .toLowerCase()
              .replace(/[^a-z0-9]/g, '-')
              .replace(/-+/g, '-')
              .replace(/^-|-$/g, '');
            const year = (p.years?.match(/\d{3,4}/)?.[0] || '');
            const simpleId = year ? `${base}-${year}` : base;
            debugList.push(`${p.name_jp} -> ${simpleId}`);

            if (simpleId === personId) return true;
            if (simpleId.startsWith(stripTrailing(personId || ''))) return true;

            // Vowel-insensitive prefix match (handles araki-soin vs araki-sin)
            const simpleNoVowels = removeVowels(simpleId);
            if (simpleNoVowels.startsWith(targetNoVowels)) return true;

            // Hyphen- and vowel-insensitive prefix match (handles nakajima-sko vs nakajima-s-...)
            const simpleCanonical = normalizeForPrefix(simpleId);
            return simpleCanonical.startsWith(targetCanonical);
          }) || null;
        }

        setDebugInfo(debugList);

        if (found) {
          console.log('PersonDetailMinimal: Found person:', found.name_jp);
          setPerson(found);
        } else {
          console.log('PersonDetailMinimal: Person not found');
          setError('Person not found');
        }
      } catch (err) {
        console.error('PersonDetailMinimal: Error loading data:', err);
        setError('Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [personId]);

  if (isLoading) {
    return (
      <div style={{ padding: '20px', background: 'white', minHeight: '100vh' }}>
        <h1>Loading Person Details...</h1>
        <p>Person ID: {personId}</p>
      </div>
    );
  }

  if (error || !person) {
    return (
      <div style={{ padding: '20px', background: 'white', minHeight: '100vh' }}>
        <h1>Error: {error || 'Person not found'}</h1>
        <p>Person ID: {personId}</p>

        <h3>Debug Info:</h3>
        <div style={{ background: '#f5f5f5', padding: '10px', marginBottom: '20px' }}>
          {debugInfo.map((info, i) => (
            <div key={i} style={{ fontSize: '12px', fontFamily: 'monospace' }}>
              {info}
            </div>
          ))}
        </div>

        <button onClick={() => navigate('/')}>Back to Home</button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', background: 'white', minHeight: '100vh' }}>
      <button
        onClick={() => navigate(-1)}
        style={{ marginBottom: '20px', padding: '10px', cursor: 'pointer' }}
      >
        ← Back
      </button>

      <h1 style={{ fontSize: '32px', marginBottom: '10px' }}>
        {person.name_romaji}
      </h1>

      <h2 style={{ fontSize: '20px', color: '#666', marginBottom: '20px' }}>
        {person.name_jp}
      </h2>

      <div style={{ marginBottom: '20px' }}>
        <strong>Years:</strong> {person.years}<br/>
        <strong>Region:</strong> {person.region}
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Biography (Japanese):</h3>
        <p>{person.description}</p>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Connections:</h3>
        <ul>
          {person.connections?.map((conn, i) => (
            <li key={i}>{conn}</li>
          ))}
        </ul>
      </div>

      <div>
        <h3>Items:</h3>
        <div style={{ display: 'grid', gap: '10px' }}>
          {person.items?.map((item, i) => {
            // Resolve to canonical item name using token vocabulary built from items.json keys
            const strip = (s) => (s || '')
              .replace(/[『』「」]/g, ' ')
              .replace(/[（）()]/g, ' ')
              .replace(/[・]/g, ' ')
              .replace(/[／/]/g, ' ')
              .replace(/\s+/g, ' ')
              .trim();

            const normalizedSource = strip(item).replace(/\s+/g, '');

            // Build vocabulary of tokens from items.json keys
            const vocabulary = new Set();
            Object.keys(itemsMap).forEach(name => {
              strip(name).split(' ').forEach(tok => {
                if (tok && tok.length >= 2) vocabulary.add(tok);
              });
            });

            // Determine which vocab tokens appear in the source label
            const matchedTokens = new Set();
            vocabulary.forEach(tok => {
              if (normalizedSource.includes(tok)) matchedTokens.add(tok);
            });

            // Score each candidate name by how many of its tokens appear in the source
            let bestName = null;
            let bestScore = -1;
            Object.keys(itemsMap).forEach(name => {
              const tokens = strip(name).split(' ').filter(Boolean);
              const score = tokens.reduce((acc, tok) => acc + (matchedTokens.has(tok) ? 1 : 0), 0);
              if (score > bestScore) {
                bestScore = score;
                bestName = name;
              }
            });

            const canonicalName = bestScore >= 2 ? bestName : item;

            const storedSlug = itemSlugs[canonicalName];
            const itemId = storedSlug || generateSimpleItemId(canonicalName);
            return (
              <Link
                key={i}
                to={`/item/${itemId}`}
                style={{
                  display: 'block',
                  padding: '12px',
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  color: '#333',
                  border: '1px solid #e0e0e0',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#e9ecef'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              >
                <strong>{item}</strong>
                <br />
                <small style={{ color: '#666' }}>Click to view item details</small>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PersonDetailMinimal;